import type { DavsTaxonomyAttribute } from '@finn-no/mobility-item-insertion-shared';
import {
  type AdInputLookup,
  assertNever,
  type EnrichedAdInputField,
  getAt,
} from '@finn-no/mobility-item-insertion-shared';
import type { Locale } from '@finn-no/number-format';
import { memo } from 'react';
import type { WidgetModel, WidgetModelArray } from '../../types/widget.js';
import { WidgetTypes } from '../../types/widget-types.js';
import { useConditions } from '../hooks/useConditions.js';
import { useRegistrationPriceHandler } from '../hooks/useRegistrationPriceHandler.js';
import { getHuckConfig } from '../utils/getHuckConfig.js';
import { mergeConditions, mergeValidationRules } from '../utils.js';

import {
  AlertWidget,
  ConditionReportWidget,
  ControlledBooleanWidget,
  ControlledComboboxWidget,
  ControlledDateWidget,
  ControlledHTMLWidget,
  ControlledModelAndMakeWidget,
  ControlledRadioButtonWidget,
  ControlledSelectWidget,
  ControlledTextAreaWidget,
  ControlledTextFieldWidget,
  ControlledToggleWidget,
  EquipmentWidget,
  HeaderWidget,
  ImageUploadWidget,
  LeasingTerminationWidget,
  PostalcodeWidget,
  PriceWidget,
  ProfileWidget,
  StreetWidget,
  UsedCarGuaranteeWidget,
  YesNoWidget,
} from './widgets/index.js';

type RenderWidgetProps = {
  field: WidgetModel;
  adInputLookup: AdInputLookup;
  className?: string;
  parent?: string;
};

const RenderField = ({ field, adInputLookup, className = 'mb-32', parent = '' }: RenderWidgetProps) => {
  const config = getHuckConfig();

  // If we are nested in a group, use dot notation to indicate hierarchy e.g. engine.fuel (see https://react-hook-form.com/docs/useform/register)
  const dotNotationName = parent ? `${parent}.${field.name}` : field.name;
  // Nested field definitions are placed in a property called children to avoid clashes with properties on the parent field
  const adInputField = getAt<EnrichedAdInputField>(adInputLookup, dotNotationName.replace('.', '.children.'));

  // Convert and merge adInput validation rules and dependencies with widget validation and conditions
  const rules = mergeValidationRules(field, adInputField, dotNotationName);
  const conditions = mergeConditions(field, adInputField);

  // Check if conditions on field are satisfied
  const satisfiesConditions = useConditions(conditions);
  if (!satisfiesConditions) {
    return null;
  }

  const fieldIsMandatory = field.mandatory ?? adInputField?.mandatory;
  const fullClassName = `${className}${field.className ? ` ${field.className}` : ''}`;

  switch (field.type) {
    case WidgetTypes.ALERT:
      if (!field.text) {
        return null;
      }
      return (
        <AlertWidget
          name={dotNotationName}
          alertType={field.alertType}
          infoModal={field.infoModal}
          title={field.title}
          text={field.text}
          links={field.links}
          conditions={conditions}
          className={fullClassName}
        />
      );
    case WidgetTypes.HEADER:
      return <HeaderWidget {...field} name={dotNotationName} />;
    case WidgetTypes.GROUP:
      return <WidgetFactory widgetModel={field.children} adInputLookup={adInputLookup} parent={dotNotationName} />;
    case WidgetTypes.STRING:
    case WidgetTypes.NUMBER:
    case WidgetTypes.DECIMAL:
    case WidgetTypes.PHONE:
      return (
        <ControlledTextFieldWidget
          name={dotNotationName}
          type={field.type}
          helpText={field.helpText}
          label={adInputField?.label ?? field.label}
          rules={rules}
          conditions={conditions}
          mandatory={fieldIsMandatory}
          suffix={field.suffix}
          className={fullClassName}
          formatAsNumber={field.formatAsNumber}
          saveAsString={field.saveAsString}
          locale={config.locale as Locale}
        />
      );
    case WidgetTypes.RADIOGROUP:
    case WidgetTypes.CHECKBOXGROUP: {
      let groupValues: { id: number; code: string; label: string }[] = [];

      /**
       * If a field has DAVS values, we use those. If not we default to the values
       * from item-management-server.
       */
      if (adInputField?.davs?.values) {
        groupValues = adInputField.davs.values.map((davsValue) => ({
          id: davsValue.id,
          code: davsValue.id.toString(),
          label: davsValue.text,
        }));
      } else if (adInputField && 'values' in adInputField) {
        groupValues = adInputField.values;
      }

      if (!adInputField || !groupValues) return null;

      return (
        <ControlledToggleWidget
          type={field.type}
          name={dotNotationName}
          values={groupValues}
          label={adInputField?.label}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          rules={rules}
          conditions={conditions}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.SELECT: {
      let values: { id: number | string; code: string; label: string }[] = [];

      /**
       * If a field has DAVS values, we use those. If not we default to the values
       * from item-management-server.
       */
      if (adInputField?.davs?.values) {
        values = adInputField.davs.values.map((davsValue) => ({
          id: davsValue.id.toString(),
          code: davsValue.id.toString(),
          label: davsValue.text,
        }));
      } else if (adInputField && 'values' in adInputField) {
        values = adInputField.values.map((value) => ({
          code: value.code,
          id: value.code,
          label: value.label,
        }));
      }

      if (!adInputField || !values) return null;

      return (
        <ControlledSelectWidget
          name={dotNotationName}
          label={adInputField.label}
          values={values}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          rules={rules}
          conditions={conditions}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.COMBOBOX: {
      let values: { id: number | string; code: string; label: string }[] = [];

      /**
       * If a field has DAVS values, we use those. If not we default to the values
       * from item-management-server.
       */
      if (adInputField?.davs?.values) {
        values = adInputField.davs.values.map((davsValue) => ({
          id: davsValue.id,
          code: davsValue.id.toString(),
          label: davsValue.text,
        }));
      } else if (adInputField && 'values' in adInputField) {
        values = adInputField.values.map((value) => ({
          code: value.code,
          id: value.code,
          label: value.label,
        }));
      }

      if (!adInputField || !values) return null;

      return (
        <ControlledComboboxWidget
          name={dotNotationName}
          label={adInputField.label}
          values={values}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          rules={rules}
          conditions={conditions}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.BOOLEAN:
      return (
        <ControlledBooleanWidget
          name={dotNotationName}
          label={adInputField?.label ?? field.label ?? ''}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          className={fullClassName}
        />
      );
    case WidgetTypes.RADIOBUTTON: {
      let groupValues: { id: number; code: string; label: string }[] = [];
      /**
       * If a field has DAVS values, we use those. If not we default to the values
       * from item-management-server.
       */
      if (adInputField?.davs?.values) {
        groupValues = adInputField.davs.values.map((davsValue) => ({
          id: davsValue.id,
          code: davsValue.id.toString(),
          label: davsValue.text,
        }));
      } else if (adInputField && 'values' in adInputField) {
        groupValues = adInputField.values;
      }

      return (
        <ControlledRadioButtonWidget
          name={dotNotationName}
          label={adInputField?.label ?? field.label ?? ''}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          asBoolean={field.asBoolean}
          values={groupValues}
          defaultValue={field.defaultValue}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.DATE:
      return (
        <ControlledDateWidget
          name={dotNotationName}
          label={adInputField?.label ?? field.label ?? ''}
          mandatory={fieldIsMandatory}
          className={fullClassName}
        />
      );
    case WidgetTypes.HTML:
      return (
        <ControlledHTMLWidget
          name={dotNotationName}
          label={adInputField?.label ?? field.label ?? ''}
          mandatory={fieldIsMandatory}
          rules={rules}
          className={fullClassName}
        />
      );
    case WidgetTypes.TEXTAREA:
      return (
        <ControlledTextAreaWidget
          name={dotNotationName}
          label={adInputField?.label ?? field.label ?? ''}
          mandatory={fieldIsMandatory}
          className={fullClassName}
        />
      );
    case WidgetTypes.MODELANDMAKE: {
      if (!adInputField || !('davs' in adInputField)) {
        return null;
      }
      return (
        <ControlledModelAndMakeWidget
          name={dotNotationName}
          attribute={adInputField.davs as DavsTaxonomyAttribute}
          label={adInputField.label}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.EQUIPMENT: {
      if (!adInputField || !('davs' in adInputField)) {
        return null;
      }
      return (
        <EquipmentWidget
          name={dotNotationName}
          attribute={adInputField.davs as DavsTaxonomyAttribute}
          headerType={field.headerType}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.IMAGE: {
      const { root, cdnUrl, itemId, orgId, csrfToken } = config;
      return (
        <ImageUploadWidget
          name={dotNotationName}
          config={{ root, cdnUrl, itemId, orgId, csrfToken }}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.PRICE:
      return <PriceWidget suffix={field.suffix} locale={config.locale as Locale} className={fullClassName} />;
    case WidgetTypes.CONDITION:
      return <ConditionReportWidget mandatory={adInputField?.mandatory} className={fullClassName} />;
    case WidgetTypes.PROFILE:
      return (
        <ProfileWidget
          name={dotNotationName}
          adInputLookup={adInputLookup}
          locale={config.locale as Locale}
          className={fullClassName}
        />
      );
    case WidgetTypes.COUNTRY: {
      if (!adInputField || !('values' in adInputField)) return null;

      return (
        <ControlledComboboxWidget
          name={dotNotationName}
          label={adInputField.label}
          values={adInputField.values.map((value) => ({
            id: value.code,
            code: value.code,
            label: value.label,
          }))}
          mandatory={fieldIsMandatory}
          helpText={field.helpText}
          rules={rules}
          conditions={conditions}
          className={fullClassName}
        />
      );
    }
    case WidgetTypes.LEASINGTERMINATION:
      return (
        <LeasingTerminationWidget
          name={dotNotationName}
          adInputLookup={adInputLookup}
          locale={config.locale as Locale}
          className={fullClassName}
        />
      );
    case WidgetTypes.USEDCARGUARANTEE:
      return <UsedCarGuaranteeWidget />;
    case WidgetTypes.STREET:
      return <StreetWidget name={dotNotationName} label={adInputField?.label} className={fullClassName} />;
    case WidgetTypes.POSTALCODE:
      return (
        <PostalcodeWidget
          name={dotNotationName}
          type={field.type}
          helpText={field.helpText}
          label={adInputField?.label ?? field.label}
          rules={rules}
          conditions={conditions}
          mandatory={fieldIsMandatory}
          className={fullClassName}
          locale={config.locale as Locale}
        />
      );
    case WidgetTypes.YESNO:
      if (!adInputField || !('values' in adInputField)) return null;
      return (
        <YesNoWidget
          name={dotNotationName}
          label={adInputField.label}
          helpText={field.helpText}
          values={adInputField.values}
          className={fullClassName}
        />
      );
    case WidgetTypes.HIDDENSTRING:
      return null;
    default:
      assertNever(field);
  }
};

type Props = {
  widgetModel: WidgetModelArray;
  adInputLookup: AdInputLookup;
  parent?: string;
};

export const WidgetFactory = memo(({ widgetModel, adInputLookup, parent = '' }: Props) => {
  useRegistrationPriceHandler();

  return (
    <>
      {(widgetModel?.length ?? 0) > 0
        ? widgetModel.map((widget, index) => {
            return (
              <div key={`${widget.name + index || index}`}>
                <RenderField field={widget} adInputLookup={adInputLookup} parent={parent} />
              </div>
            );
          })
        : null}
    </>
  );
});
